import { agentCaller } from '../../../lib/ai/agentCaller';
import { processJsonOutput } from '../../../components/agents/documentAi';
import { supabase } from '../../../lib/supabase';

const buildPrompt = (settings, previousOutputs = []) => {
  let prompt = 'Create a single Social Media post for with this brief: ' + settings.userPrompt || '';
  let formatInstructions = `
  Output Rules:
  1. Output only valid JSON that can be parsed with JSON.parse()
  4. Do not include any explanatory text before or after the JSON
  5. Do not use comments or trailing commas
  6. Use double quotes for property names and string values
  7. Your response is a title for internal purposes and content which makes up the whole social post
  
  Example response: 
    {
      title: string,
      content: string (including hashtags/emojis)
    }`;

  // Add selected upstream node outputs
  if (previousOutputs.length > 0) {
    console.log('Building prompt with outputs:', previousOutputs);
    previousOutputs.forEach(output => {
      // The output structure is different - we need to access the actual data
      const outputData = output.execution?.output;
      
      if (!outputData) {
        console.log('Skipping invalid output:', output);
        return;
      }

      if (output.type === 'getdocument') {
        console.log('Processing document output:', outputData);
        prompt += `\n\nTurn this content into a social post:\n${outputData}\n`;
      } else if (output.type === 'getdata') {
        const dataStr = Array.isArray(outputData) 
          ? outputData.map(item => JSON.stringify(item, null, 2)).join('\n')
          : JSON.stringify(outputData, null, 2);
        prompt += `\n\nConsider this data in your response:\n${dataStr}\n`;
      } else if (output.type === 'agent') {
        prompt += `\n\nIncorporate this previous agent's response:\n${outputData}\n`;
      } else {
        prompt += `\n\nAdditional information:\n${JSON.stringify(outputData, null, 2)}\n`;
      }
    });
  }

  // Add format instructions at the end to emphasize their importance
  prompt += `\n\n${formatInstructions}`;

  console.log('Final prompt:', prompt);
  return prompt;
};

export async function executeNode({
  settings, 
  allPreviousOutputs = {}, 
  projectId,
  organizationId,
  userId
}) {
  console.log('=== SOCIAL NODE EXECUTION START ===');
  console.log('Social settings:', settings);
  console.log('All previous outputs:', allPreviousOutputs);
  console.log('Selected upstream nodes:', settings.selectedUpstreamNodes);
  console.log('Selected network:', settings.network);
  console.log('Project ID:', projectId);
  console.log('Organization ID:', organizationId);
  console.log('User ID:', userId);
  

  try {
    // Get selected upstream node outputs
    const selectedOutputs = settings.selectedUpstreamNodes
      .map(nodeId => {
        console.log('Looking for output from node:', nodeId);
        const output = allPreviousOutputs[nodeId];
        console.log('Found output for node', nodeId, ':', output);

        // Basic validation
        if (!output) {
          console.warn(`No output found for node ${nodeId}`);
          return null;
        }

        // Data validation and logging
        if (!output.execution?.output) {
          console.warn(`Invalid or missing output data for node ${nodeId}`);
          return null;
        }

        // Log the structure of the output
        console.log('Output type:', output.type);
        console.log('Output data structure:', {
          hasExecution: !!output.execution,
          hasOutput: !!output.execution?.output,
          outputType: typeof output.execution?.output,
          isArray: Array.isArray(output.execution?.output)
        });

        return {
          type: output.type || 'unknown',
          execution: output.execution
        };
      })
      .filter(output => output !== null);

    console.log('Filtered selected outputs:', selectedOutputs);

    if (selectedOutputs.length === 0) {
      console.warn('No valid outputs found from selected upstream nodes');
      console.warn('Available outputs:', allPreviousOutputs);
    }

    // Build the prompt with format instructions
    const prompt = buildPrompt(settings, selectedOutputs);
    console.log('Built prompt:', prompt);

    const networkType = settings.network;

    // Add format-specific system prompt additions
    let systemPrompt = 'You are a Social Content Writing Agent specialising in ' + networkType + ' that creates engaging, platform-optimised posts. You adapt content briefs into compelling social copy that drives engagement while maintaining brand voice and meeting marketing objectives.';
    
    // Call the agent
    const payload = {
      userPrompt: prompt,
      systemPrompt,
      agentName: settings.agentType || 'dave',
      outputFormat: 'json',
      projectId,
      organizationId,
      currentUserId: userId
    };

    const result = await agentCaller(payload);

    if (!result.success) {
      throw new Error(result.error || 'Failed to execute agent');
    }

    const processedOutput = processJsonOutput(result.output);

    // Save the content to the database
    const { error: saveError } = await supabase
      .from('content')
      .insert({
        project_id: projectId,
        title: processedOutput.title,
        content: processedOutput.content,
        node_type: settings.network, // e.g., 'linkedin', 'twitter', etc.
        status: 'draft',
        created_by: userId,
        updated_by: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

    if (saveError) {
      console.error('Error saving social content:', saveError);
      throw new Error('Failed to save social content');
    }

    return {
      success: true,
      output: processedOutput,
      type: 'social',
      nodeId: settings.id
    };

  } catch (error) {
    console.error('Error executing social node:', error);
    throw error;
  }
}
